import * as THREE from 'three';
import { RARITY_COLORS, type WeaponRarity } from './FpsWeaponRarity.js';

/** Deuteranopia-friendly rarity palette (swaps green/purple-heavy pairs). */
export const RARITY_COLORS_COLORBLIND: Record<WeaponRarity, string> = {
  common: '#c8c8c8',
  uncommon: '#e6b800',
  rare: '#00b4d8',
  epic: '#ff6b9d',
  legendary: '#ff6b35',
};

export function isColorblindModeEnabled(): boolean {
  return typeof document !== 'undefined' && document.body.dataset.fpsColorblind === 'true';
}

export function getRarityCssColor(rarity: WeaponRarity): string {
  return isColorblindModeEnabled() ? RARITY_COLORS_COLORBLIND[rarity] : RARITY_COLORS[rarity];
}

/** 4-step luminance ramp for MeshToonMaterial (cartoon shading bands). */
export function createToonGradientMap(): THREE.DataTexture {
  const data = new Uint8Array([
    40, 40, 48, 255,
    110, 110, 120, 255,
    190, 195, 210, 255,
    255, 255, 255, 255,
  ]);
  const tex = new THREE.DataTexture(data, 4, 1, THREE.RGBAFormat);
  tex.needsUpdate = true;
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  return tex;
}

export const windUniform = { value: 0 };

export function toonMat(
  color: number,
  gradient: THREE.DataTexture,
  opts?: { emissive?: number; emissiveIntensity?: number },
): THREE.MeshToonMaterial {
  const m = new THREE.MeshToonMaterial({
    color,
    gradientMap: gradient,
  });
  if (opts?.emissive !== undefined) m.emissive.setHex(opts.emissive);
  m.emissiveIntensity = opts?.emissiveIntensity ?? (opts?.emissive !== undefined ? 0.18 : 0);
  return m;
}

export function foliageToonMat(
  color: number,
  gradient: THREE.DataTexture,
  opts?: { emissive?: number; emissiveIntensity?: number },
): THREE.MeshToonMaterial {
  const m = toonMat(color, gradient, opts);
  m.onBeforeCompile = (shader) => {
    shader.uniforms.windTime = windUniform;
    shader.vertexShader = 'uniform float windTime;\n' + shader.vertexShader;
    shader.vertexShader = shader.vertexShader.replace(
      '#include <begin_vertex>',
      `
      #include <begin_vertex>
      float heightFactor = max(0.0, position.y - 0.08);
      float swayAmt = sin(windTime * 2.2 + position.x * 0.35 + position.y * 0.9) * 0.085 * heightFactor;
      float swayZ = cos(windTime * 1.85 + position.z * 0.28 + position.y * 0.7) * 0.048 * heightFactor;
      transformed.x += swayAmt;
      transformed.z += swayAmt * 0.42 + swayZ;
      `
    );
  };
  return m;
}

/** Reusable world prop materials — avoids duplicate toonMat instances across clusters. */
export type SharedWorldMaterials = {
  grad: THREE.DataTexture;
  woodPost: THREE.MeshToonMaterial;
  windowGlass: THREE.MeshStandardMaterial;
  coverCrate: THREE.MeshToonMaterial;
  coverTarp: THREE.MeshToonMaterial;
  coverBarrel: THREE.MeshToonMaterial;
  trunkBrown: THREE.MeshToonMaterial;
  foliageGreen: THREE.MeshToonMaterial;
  rockGray: THREE.MeshToonMaterial;
};

export function createSharedWorldMaterials(grad: THREE.DataTexture): SharedWorldMaterials {
  return {
    grad,
    woodPost: toonMat(0x5c3a24, grad, { emissive: 0x1d1006, emissiveIntensity: 0.1 }),
    windowGlass: new THREE.MeshStandardMaterial({
      color: 0x89d4ff,
      emissive: 0xaaddff,
      emissiveIntensity: 0.55,
      roughness: 0.25,
      metalness: 0.15,
    }),
    coverCrate: toonMat(0xc9a86c, grad, {
      emissive: new THREE.Color(0xc9a86c).multiplyScalar(0.16).getHex(),
      emissiveIntensity: 0.1,
    }),
    coverTarp: toonMat(0x6a7a8a, grad, {
      emissive: new THREE.Color(0x6a7a8a).multiplyScalar(0.22).getHex(),
      emissiveIntensity: 0.12,
    }),
    coverBarrel: toonMat(0x8b4513, grad, {
      emissive: new THREE.Color(0x8b4513).multiplyScalar(0.18).getHex(),
      emissiveIntensity: 0.1,
    }),
    trunkBrown: toonMat(0x6d4c41, grad, { emissive: 0x221108, emissiveIntensity: 0.15 }),
    foliageGreen: foliageToonMat(0x4f8f50, grad, { emissive: 0x173814, emissiveIntensity: 0.16 }),
    rockGray: toonMat(0x8d949b, grad, {
      emissive: new THREE.Color(0x8d949b).multiplyScalar(0.1).getHex(),
      emissiveIntensity: 0.1,
    }),
  };
}
